/**
 * Payment calculation utilities
 * Parses pay strings like "$75/hr + 1" and calculates job payments
 */

import { ParsedPayString, CalculatedPay } from '../types/payment';

/**
 * Parse a pay string to extract rate information
 * Handles formats like:
 * - "$75/hr" (simple hourly)
 * - "$75/hr + 1" (hourly with 1 bonus hour)
 * - "$300 flat" (flat rate)
 * - "$75-$85/hr" (range - requires verification)
 * - "TBD", "varies", etc. (unknown - requires manual entry)
 */
export function parsePayString(payString: string): ParsedPayString {
  if (!payString || typeof payString !== 'string') {
    return {
      type: 'unknown',
      requiresManualVerification: true,
      originalString: payString || '',
    };
  }

  const trimmed = payString.trim().toLowerCase();

  // Check for flat rate patterns
  const flatPatterns = [/\$(\d+)\s*flat/i, /^(\d+)\s*flat/i, /flat\s*\$?(\d+)/i];
  for (const pattern of flatPatterns) {
    const match = trimmed.match(pattern);
    if (match) {
      return {
        type: 'flat',
        flatAmount: parseFloat(match[1]),
        requiresManualVerification: false,
        originalString: payString,
      };
    }
  }

  // Check for "for the day" / "per day" / "/day" patterns: "$250 for the day", "$100/day"
  const dayRateMatch = trimmed.match(/\$?(\d+(?:\.\d+)?)\s*(?:for\s+the\s+day|per\s+day|\/\s*day)/i);
  if (dayRateMatch) {
    return {
      type: 'flat',
      flatAmount: parseFloat(dayRateMatch[1]),
      requiresManualVerification: false,
      originalString: payString,
    };
  }

  // Check for range pattern: "$75-$85/hr"
  const rangeMatch = trimmed.match(/\$?(\d+(?:\.\d+)?)\s*-\s*\$?(\d+(?:\.\d+)?)\s*\/\s*h(?:ou)?r?/i);
  if (rangeMatch) {
    return {
      type: 'range',
      hourlyRate: parseFloat(rangeMatch[1]),
      hourlyRateMax: parseFloat(rangeMatch[2]),
      requiresManualVerification: true, // Ranges require scheduler to pick a value
      originalString: payString,
    };
  }

  // Check for hourly rate with optional bonus hours: "$75/hr + 1"
  const hourlyMatch = trimmed.match(/\$?(\d+(?:\.\d+)?)\s*\/\s*h(?:ou)?r?\s*(?:\+\s*(\d+(?:\.\d+)?))?/i);
  if (hourlyMatch) {
    const hourlyRate = parseFloat(hourlyMatch[1]);
    const bonusHours = hourlyMatch[2] ? parseFloat(hourlyMatch[2]) : 0;
    
    return {
      type: 'hourly',
      hourlyRate,
      bonusHours,
      requiresManualVerification: false,
      originalString: payString,
    };
  }

  // Check for unknown/manual entry patterns
  const unknownPatterns = ['tbd', 'varies', 'see notes', 'negotiable', 'n/a', 'na'];
  if (unknownPatterns.some(pattern => trimmed.includes(pattern))) {
    return {
      type: 'unknown',
      requiresManualVerification: true,
      originalString: payString,
    };
  }

  // Check for a plain dollar amount: "$300", "300", "$75.50"
  const plainAmountMatch = trimmed.match(/^\$?(\d+(?:\.\d+)?)$/);
  if (plainAmountMatch) {
    return {
      type: 'flat',
      flatAmount: parseFloat(plainAmountMatch[1]),
      requiresManualVerification: false,
      originalString: payString,
    };
  }

  // If nothing matches, treat as unknown
  return {
    type: 'unknown',
    requiresManualVerification: true,
    originalString: payString,
  };
}

/**
 * Calculate hours worked between two ISO datetime strings
 * Returns decimal hours (e.g., 8.5 for 8 hours 30 minutes)
 */
export function calculateHoursWorked(startDateTime: string, finishDateTime: string): number {
  try {
    const start = new Date(startDateTime);
    const finish = new Date(finishDateTime);
    
    if (isNaN(start.getTime()) || isNaN(finish.getTime())) {
      return 0;
    }
    
    const diffMs = finish.getTime() - start.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    
    return Math.max(0, diffHours); // Ensure non-negative
  } catch (error) {
    console.error('Error calculating hours worked:', error);
    return 0;
  }
}

/**
 * Calculate payment for a job based on pay string and time worked
 * "+1" in pay string means add 1 additional hour to the calculation
 */
export function calculateJobPay(
  payString: string,
  startDateTime: string,
  finishDateTime: string,
  _notes?: string
): CalculatedPay {
  const parsed = parsePayString(payString);
  const hoursWorked = calculateHoursWorked(startDateTime, finishDateTime);

  // Handle flat rate
  if (parsed.type === 'flat' && parsed.flatAmount) {
    return {
      amount: parsed.flatAmount,
      needsVerification: false,
      hoursWorked,
      bonusHours: 0,
      totalHours: hoursWorked,
      explanation: `Flat rate: ${formatCurrency(parsed.flatAmount)}`,
    };
  }

  // Handle hourly rate
  if (parsed.type === 'hourly' && parsed.hourlyRate) {
    const bonusHours = parsed.bonusHours || 0;
    const totalHours = hoursWorked + bonusHours;
    const amount = parsed.hourlyRate * totalHours;
    
    let explanation = `${formatCurrency(parsed.hourlyRate)}/hr × ${totalHours.toFixed(2)} hrs`;
    if (bonusHours > 0) {
      explanation += ` (${hoursWorked.toFixed(2)} worked + ${bonusHours} bonus)`;
    }
    explanation += ` = ${formatCurrency(amount)}`;
    
    return {
      amount: Math.round(amount * 100) / 100, // Round to 2 decimal places
      needsVerification: false,
      hoursWorked,
      bonusHours,
      totalHours,
      explanation,
    };
  }

  // Handle range (requires verification)
  if (parsed.type === 'range' && parsed.hourlyRate && parsed.hourlyRateMax) {
    const midpoint = (parsed.hourlyRate + parsed.hourlyRateMax) / 2;
    const amount = midpoint * hoursWorked;
    
    return {
      amount: Math.round(amount * 100) / 100,
      needsVerification: true,
      hoursWorked,
      bonusHours: 0,
      totalHours: hoursWorked,
      explanation: `Range ${formatCurrency(parsed.hourlyRate)}-${formatCurrency(parsed.hourlyRateMax)}/hr (midpoint estimate)`,
    };
  }

  // Unknown type - return 0 and require manual entry
  return {
    amount: 0,
    needsVerification: true,
    hoursWorked,
    bonusHours: 0,
    totalHours: hoursWorked,
    explanation: `Pay: "${payString}" - requires manual entry`,
  };
}

/**
 * Format a number as currency (USD)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format hours as a readable string
 * Examples: "8.00 hrs", "8.50 hrs"
 */
export function formatHours(hours: number): string {
  return `${hours.toFixed(2)} hrs`;
}

/**
 * Get the start of the current year (Jan 1, 00:00:00)
 */
export function getCurrentYearStart(): number {
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
  return yearStart.getTime();
}

/**
 * Get the current timestamp
 */
export function getCurrentTimestamp(): number {
  return Date.now();
}

/**
 * Format a timestamp as a readable date string
 * Example: "Mar 31, 2026"
 */
export function formatDate(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
}

/**
 * Format a timestamp as a readable date and time string
 * Example: "Mar 31, 2026 at 2:30 PM"
 */
export function formatDateTime(timestamp: number): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
}

/**
 * Format ISO datetime string as readable date and time
 * Example: "Mar 31, 2026 at 2:30 PM"
 */
export function formatISODateTime(isoString: string): string {
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return isoString;
    return formatDateTime(date.getTime());
  } catch (error) {
    return isoString;
  }
}

/**
 * Format a date range from two ISO strings
 * Example: "Mar 31, 2:00 PM - 10:00 PM"
 */
export function formatDateRange(startISO: string, finishISO: string): string {
  try {
    const start = new Date(startISO);
    const finish = new Date(finishISO);
    
    if (isNaN(start.getTime()) || isNaN(finish.getTime())) {
      return `${startISO} - ${finishISO}`;
    }
    
    const isSameDay = 
      start.getFullYear() === finish.getFullYear() &&
      start.getMonth() === finish.getMonth() &&
      start.getDate() === finish.getDate();
    
    if (isSameDay) {
      const dateStr = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
      }).format(start);
      
      const startTime = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }).format(start);
      
      const finishTime = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      }).format(finish);
      
      return `${dateStr}, ${startTime} - ${finishTime}`;
    }
    
    // Different days
    return `${formatISODateTime(startISO)} - ${formatISODateTime(finishISO)}`;
  } catch (error) {
    return `${startISO} - ${finishISO}`;
  }
}
